import React from 'react';
import { Target, Zap, Wrench, Phone, MessageCircle, Star, ArrowRight } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { FeatureCard } from '../components/ui/FeatureCard';

export const AboutPage: React.FC = () => {
    usePageMetadata({
        title: "O nas | Biznes bez Klikania",
        description: "Poznaj nas. Wdrażamy tylko sprawdzone automatyzacje AI które się zwracają. Zobacz nasze podejście i realne historie sukcesu.",
        schema: {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "O Nas – Realne Automatyzacje AI | BiznesBezKlikania.pl",
            "description": "Poznaj naszą historię i podejście do automatyzacji biznesu z wykorzystaniem sztucznej inteligencji.",
            "url": "https://biznesbezklikania.pl/o-nas"
        },
        ogData: {
            type: 'profile'
        }
    });

    const values = [
        { icon: <Target className="w-8 h-8" />, title: 'Zwraca się albo oddajemy pieniądze', description: 'Jeśli automatyzacja nie zwróci się w 3 miesiące, oddajemy pieniądze. Proste.' },
        { icon: <Zap className="w-8 h-8" />, title: 'MVP w tydzień', description: 'Najpierw działający prototyp, potem ulepszenia. Bez miesięcy planowania i zbędnych spotkań.' },
        { icon: <Wrench className="w-8 h-8" />, title: 'Własne testy', description: 'Każde rozwiązanie najpierw wdrażamy i testujemy na własnych, realnych projektach biznesowych.' },
        { icon: <Phone className="w-8 h-8" />, title: 'Bezpośredni kontakt', description: 'Dzwonisz do mnie, nie do anonimowego "działu obsługi". Odpowiadam osobiście.' },
        { icon: <MessageCircle className="w-8 h-8" />, title: 'Bez marketingowej ściemy', description: 'Nie obiecujemy cudów. Mówimy wprost, co się da zrobić, a co jest jeszcze fantazją.' },
        { icon: <Star className="w-8 h-8" />, title: 'Praktyka ponad teorią', description: 'Wierzymy w namacalne rezultaty i działające rozwiązania, a nie w puste prezentacje i modne hasła.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-32">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Bez ściemy. Automatyzacje, które działają.
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            To nie jest kolejna "agencja AI" z prezentacjami. To realne automatyzacje testowane na własnych projektach. Poznaj prawdziwą historię.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Story Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <RevealOnScroll>
                            <div className="prose dark:prose-invert max-w-none">
                                <h2 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-6">
                                    Jak to naprawdę wygląda?
                                </h2>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                    Zaczęło się w 2022, kiedy zobaczyłem, że mój sklep z obuwiem traci klientów przez wolne odpowiedzi na pytania. Zatrudnienie kolejnej osoby do obsługi? 15,000 zł miesięcznie z kosztami. Zbudowałem pierwszego agenta AI za 500 zł.
                                </p>
                                <GlassCard variant="subtle" className="my-6 border-l-4 border-primary-light dark:border-primary-dark">
                                    <p className="italic text-text-secondary-light dark:text-text-secondary-dark">
                                        AI to nie magia. To narzędzie, które albo rozwiązuje realny problem, albo jest bezużytecznym gadżetem. Nie sprzedaję gadżetów - wdrażam tylko to, co sam przetestowałem.
                                    </p>
                                </GlassCard>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                    Od tamtego czasu zbudowałem dziesiątki automatyzacji. Niektóre okazały się strzałem w dziesiątkę, inne totalną klapą. Dzielę się tylko tymi sprawdzonymi, które przynoszą mierzalne rezultaty.
                                </p>
                            </div>
                        </RevealOnScroll>

                        <RevealOnScroll delay={200} direction="right">
                            <GlassCard variant="medium" className="overflow-hidden p-0">
                                <img
                                    src="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
                                    alt="Zespół BiznesBezKlikania.pl pracuje nad innowacyjnymi rozwiązaniami z zakresu automatyzacji AI"
                                    className="w-full h-auto"
                                    loading="lazy"
                                />
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Nasze podejście do automatyzacji
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Mniej gadania o "transformacji cyfrowej", więcej działających botów i realnych oszczędności.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {values.map((value, index) => (
                            <RevealOnScroll key={value.title} delay={index * 100}>
                                <FeatureCard
                                    icon={value.icon}
                                    title={value.title}
                                    description={value.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};
